import React, { useState, useMemo } from 'react';
import { formatIST } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  GitBranch,
  ChevronDown,
  ChevronRight,
  User,
  Clock,
  MessageSquare,
  Layers,
  ArrowRight,
} from 'lucide-react';

/**
 * Build a tree structure from flat batch list
 * Returns { roots: [...], nodeMap: {...} }
 */
function buildBatchTree(batches) {
  if (!batches || batches.length === 0) return { roots: [], nodeMap: {} };

  const nodeMap = {};
  
  // Create nodes for all batches
  batches.forEach(batch => {
    nodeMap[batch.id] = {
      ...batch,
      children: [],
    };
  });

  const roots = [];

  // Build parent-child relationships
  batches.forEach(batch => {
    const parentId = batch.parent_batch_id;
    if (parentId && nodeMap[parentId]) {
      nodeMap[parentId].children.push(nodeMap[batch.id]);
    } else if (!parentId) {
      roots.push(nodeMap[batch.id]);
    } else {
      // Parent not found (maybe from old data), treat as root
      roots.push(nodeMap[batch.id]);
    }
  });

  // Sort children by batch_number
  Object.values(nodeMap).forEach(node => {
    node.children.sort((a, b) => a.batch_number - b.batch_number);
  });

  // Sort roots by batch_number
  roots.sort((a, b) => a.batch_number - b.batch_number);

  return { roots, nodeMap };
}

/**
 * Get the full lineage path for a batch
 */
function getLineagePath(batch, nodeMap) {
  const path = [];
  let current = batch;
  
  while (current) {
    path.unshift({
      id: current.id,
      batch_number: current.batch_number,
      quantity: current.quantity,
      status: current.status,
    });
    
    if (current.parent_batch_id && nodeMap[current.parent_batch_id]) {
      current = nodeMap[current.parent_batch_id];
    } else {
      current = null;
    }
  }
  
  return path;
}

/**
 * Split info badge - compact indicator that batch was split
 */
export function SplitBadge({ batch, showDetails = false }) {
  if (!batch.split_from && !batch.has_splits) return null;

  const isSplitChild = !!batch.split_from;
  const hasChildren = batch.has_splits || (batch.child_batch_ids?.length > 0);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className={`text-[10px] cursor-help ${
              isSplitChild 
                ? 'bg-purple-50 text-purple-700 border-purple-200' 
                : 'bg-orange-50 text-orange-700 border-orange-200'
            }`}
          >
            <GitBranch className="h-3 w-3 mr-1" />
            {isSplitChild ? `From #${batch.split_from.parent_batch_number}` : 'Split'}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {isSplitChild ? (
            <div className="text-xs space-y-1">
              <p className="font-medium">Split from Batch #{batch.split_from.parent_batch_number}</p>
              <p className="text-slate-500">
                {batch.split_from.passed_qty?.toLocaleString()} passed, {batch.split_from.remaining_qty?.toLocaleString()} remained
              </p>
              <p className="text-slate-500">
                At: {batch.split_from.split_at_stage}
              </p>
              <p className="text-slate-500">
                By: {batch.split_from.split_by_name}
              </p>
              {batch.split_from.remarks && (
                <p className="text-slate-400 italic">"{batch.split_from.remarks}"</p>
              )}
            </div>
          ) : (
            <div className="text-xs">
              <p className="font-medium">This batch has been split</p>
              <p className="text-slate-500">
                {batch.child_batch_ids?.length || 0} child batch(es) created
              </p>
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Lineage path - shows the path from root to current batch
 */
export function BatchLineagePath({ batch, allBatches }) {
  const { nodeMap } = useMemo(() => buildBatchTree(allBatches), [allBatches]);
  const lineage = useMemo(() => getLineagePath(batch, nodeMap), [batch, nodeMap]);

  if (lineage.length <= 1) return null;

  return (
    <div className="flex items-center gap-1 text-[10px] text-slate-500 flex-wrap">
      <span className="text-slate-400">Lineage:</span>
      {lineage.map((node, idx) => (
        <React.Fragment key={node.id}>
          <span className={`px-1.5 py-0.5 rounded ${
            node.id === batch.id 
              ? 'bg-blue-100 text-blue-700 font-medium' 
              : 'bg-slate-100 text-slate-600'
          }`}>
            #{node.batch_number} ({node.quantity?.toLocaleString()})
          </span>
          {idx < lineage.length - 1 && (
            <ArrowRight className="h-3 w-3 text-slate-300" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/**
 * Single batch node in the tree view
 */
function BatchTreeNode({ 
  node, 
  depth = 0, 
  isLast = false, 
  parentLines = [],
  expanded,
  onToggle,
  onBatchClick 
}) {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded[node.id];
  const splitInfo = node.split_from;

  const statusColor = {
    pending: 'bg-slate-100 text-slate-600 border-slate-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-green-50 text-green-700 border-green-200',
  };

  return (
    <div className="relative">
      {/* Tree lines */}
      <div className="flex">
        {/* Vertical connector lines from parents */}
        {parentLines.map((showLine, idx) => (
          <div key={idx} className="w-4 flex-shrink-0 relative">
            {showLine && (
              <div className="absolute left-2 top-0 bottom-0 w-px bg-slate-200" />
            )}
          </div>
        ))}

        {/* Current level connector */}
        {depth > 0 && (
          <div className="w-4 flex-shrink-0 relative">
            <div className={`absolute left-2 top-0 ${isLast ? 'h-4' : 'h-full'} w-px bg-slate-200`} />
            <div className="absolute left-2 top-4 w-2 h-px bg-slate-200" />
          </div>
        )}

        {/* Node content */}
        <div className="flex-1 min-w-0 py-1">
          <div 
            className={`flex items-center gap-2 p-2 rounded-lg border bg-white hover:bg-slate-50/50 transition-colors ${
              hasChildren ? 'cursor-pointer' : ''
            }`}
            onClick={() => hasChildren && onToggle(node.id)}
          >
            {/* Expand/collapse toggle */}
            {hasChildren ? (
              <button 
                className="p-0.5 rounded hover:bg-slate-100"
                onClick={(e) => { e.stopPropagation(); onToggle(node.id); }}
              >
                {isExpanded ? (
                  <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                )}
              </button>
            ) : (
              <div className="w-4" />
            )}

            {/* Batch info */}
            <div className="flex items-center gap-2 flex-1 min-w-0 flex-wrap">
              <div className="flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">
                  Batch #{node.batch_number}
                </span>
              </div>

              <Badge className={`text-[10px] ${statusColor[node.status] || statusColor.pending}`}>
                {node.status?.replace('_', ' ') || 'pending'}
              </Badge>

              <span className="text-xs text-slate-500">
                Qty: {node.quantity?.toLocaleString()}
              </span>

              {node.status !== 'completed' && node.current_stage_name && (
                <span className="text-xs font-medium text-blue-600">
                  @ {node.current_stage_name}
                </span>
              )}

              {/* Split indicator */}
              {splitInfo && (
                <Badge 
                  variant="outline" 
                  className="text-[10px] bg-purple-50 text-purple-700 border-purple-200"
                >
                  <GitBranch className="h-3 w-3 mr-1" />
                  From #{splitInfo.parent_batch_number}
                </Badge>
              )}

              {node.has_splits && (
                <Badge 
                  variant="outline" 
                  className="text-[10px] bg-orange-50 text-orange-700 border-orange-200"
                >
                  <GitBranch className="h-3 w-3 mr-1" />
                  {node.child_batch_ids?.length || node.children?.length || 0} splits
                </Badge>
              )}
            </div>

            {/* View details button */}
            {onBatchClick && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 text-xs text-slate-500"
                onClick={(e) => { e.stopPropagation(); onBatchClick(node); }}
              >
                Details
              </Button>
            )}
          </div>

          {/* Split details (when this is a child batch) */}
          {splitInfo && isExpanded && (
            <div className="mt-1 ml-6 p-2 bg-purple-50/50 rounded-md border border-purple-100 text-xs">
              <div className="flex items-center gap-3 flex-wrap text-slate-600">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {splitInfo.split_at ? formatIST(splitInfo.split_at, { dateOnly: true }) : '-'}
                </span>
                <span className="flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {splitInfo.split_by_name || 'Unknown'}
                </span>
                <span>
                  At stage: <span className="font-medium">{splitInfo.split_at_stage}</span>
                </span>
                <span>
                  {splitInfo.passed_qty?.toLocaleString()} passed, {splitInfo.remaining_qty?.toLocaleString()} remained
                </span>
              </div>
              {splitInfo.remarks && (
                <div className="flex items-start gap-1 mt-1 text-slate-500">
                  <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span className="italic">"{splitInfo.remarks}"</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Children */}
      {hasChildren && isExpanded && (
        <div className="ml-0">
          {node.children.map((child, idx) => (
            <BatchTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              isLast={idx === node.children.length - 1}
              parentLines={[...parentLines, !isLast]}
              expanded={expanded}
              onToggle={onToggle}
              onBatchClick={onBatchClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Full batch tree view - shows all batches with their relationships
 */
export function BatchTreeView({ batches, onBatchClick, defaultExpanded = true }) {
  const { roots, nodeMap } = useMemo(() => buildBatchTree(batches), [batches]);
  
  // Track expanded state for each node
  const [expanded, setExpanded] = useState(() => {
    if (!defaultExpanded) return {};
    // Default expand all
    const exp = {};
    Object.keys(nodeMap).forEach(id => { exp[id] = true; });
    return exp;
  });

  const toggleExpand = (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const hasSplits = batches.some(b => b.split_from || b.has_splits || b.child_batch_ids?.length > 0);

  if (!batches || batches.length === 0) {
    return (
      <div className="text-sm text-slate-500 text-center py-4">
        No batches found
      </div>
    );
  }

  // If no splits, show simple list
  if (!hasSplits) {
    return (
      <div className="space-y-2">
        {batches.map(batch => (
          <div 
            key={batch.id} 
            className="flex items-center gap-2 p-2 rounded-lg border bg-white"
          >
            <Layers className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">
              Batch #{batch.batch_number}
            </span>
            <Badge className={`text-[10px] ${
              batch.status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
              batch.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
              'bg-slate-100 text-slate-600 border-slate-200'
            }`}>
              {batch.status?.replace('_', ' ') || 'pending'}
            </Badge>
            <span className="text-xs text-slate-500">
              Qty: {batch.quantity?.toLocaleString()}
            </span>
            {batch.status !== 'completed' && batch.current_stage_name && (
              <span className="text-xs font-medium text-blue-600">
                @ {batch.current_stage_name}
              </span>
            )}
            {onBatchClick && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto h-6 text-xs text-slate-500"
                onClick={() => onBatchClick(batch)}
              >
                Details
              </Button>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Show tree view for split batches
  return (
    <div className="space-y-1">
      {/* Header with collapse all / expand all */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <GitBranch className="h-3.5 w-3.5 text-orange-500" />
          <span>Batch Split History</span>
          <Badge variant="outline" className="text-[10px]">
            {batches.length} batch{batches.length !== 1 ? 'es' : ''}
          </Badge>
        </div>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => {
              const allExpanded = {};
              Object.keys(nodeMap).forEach(id => { allExpanded[id] = true; });
              setExpanded(allExpanded);
            }}
          >
            Expand All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={() => setExpanded({})}
          >
            Collapse All
          </Button>
        </div>
      </div>

      {/* Tree */}
      {roots.map((root, idx) => (
        <BatchTreeNode
          key={root.id}
          node={root}
          depth={0}
          isLast={idx === roots.length - 1}
          parentLines={[]}
          expanded={expanded}
          onToggle={toggleExpand}
          onBatchClick={onBatchClick}
        />
      ))}
    </div>
  );
}

/**
 * Compact split summary - for showing in table rows or cards
 */
export function BatchSplitSummary({ batches }) {
  const hasSplits = batches?.some(b => b.split_from || b.has_splits || b.child_batch_ids?.length > 0);
  
  if (!hasSplits) return null;

  const splitCount = batches.filter(b => b.split_from).length;
  const rootBatches = batches.filter(b => !b.split_from);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge 
            variant="outline" 
            className="text-[10px] bg-orange-50 text-orange-700 border-orange-200 cursor-help"
          >
            <GitBranch className="h-3 w-3 mr-1" />
            {splitCount} split{splitCount !== 1 ? 's' : ''}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="text-xs space-y-1">
            <p className="font-medium">Batch Split Summary</p>
            <p className="text-slate-500">
              Original batches: {rootBatches.length}
            </p>
            <p className="text-slate-500">
              Split batches: {splitCount}
            </p>
            <p className="text-slate-500">
              Total batches: {batches.length}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default {
  SplitBadge,
  BatchLineagePath,
  BatchTreeView,
  BatchSplitSummary,
};
